import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import authService from '../services/authService';
import AdminLayout from '../components/AdminLayout';

interface SystemHealth {
  database_status: string;
  redis_status: string;
  whatsapp_api_status: string;
  last_checked: string;
}

const AdminHealth: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      const response = await axios.get<SystemHealth>(
        `${API_BASE_URL}/admin/dashboard/health`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setHealth(response.data);
    } catch (err) {
      console.error('Error fetching health:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'healthy') return 'bg-green-100 text-green-800 border-green-200';
    if (status === 'unhealthy') return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status: string) => {
    if (status === 'healthy') return '✓';
    if (status === 'unhealthy') return '✗';
    return '?';
  };

  if (loading && !health) {
    return (
      <AdminLayout>
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow h-40"></div>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Database Status */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className={`p-6 border-l-4 ${getStatusColor(health?.database_status || 'unknown')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 uppercase">Database</p>
                  <p className="text-2xl font-bold mt-2 capitalize">{health?.database_status || 'Unknown'}</p>
                </div>
                <div className="text-4xl">
                  {getStatusIcon(health?.database_status || 'unknown')}
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center text-sm text-gray-500">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                  PostgreSQL
                </div>
              </div>
            </div>
          </div>

          {/* Redis Status */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className={`p-6 border-l-4 ${getStatusColor(health?.redis_status || 'unknown')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 uppercase">Redis</p>
                  <p className="text-2xl font-bold mt-2 capitalize">{health?.redis_status || 'Unknown'}</p>
                </div>
                <div className="text-4xl">
                  {getStatusIcon(health?.redis_status || 'unknown')}
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center text-sm text-gray-500">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                  </svg>
                  Cache & Queue
                </div>
              </div>
            </div>
          </div>

          {/* WhatsApp API Status */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className={`p-6 border-l-4 ${getStatusColor(health?.whatsapp_api_status || 'unknown')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 uppercase">WhatsApp API</p>
                  <p className="text-2xl font-bold mt-2 capitalize">{health?.whatsapp_api_status || 'Unknown'}</p>
                </div>
                <div className="text-4xl">
                  {getStatusIcon(health?.whatsapp_api_status || 'unknown')}
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center text-sm text-gray-500">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  External Service
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Last Checked */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Last Health Check</p>
                <p className="text-lg font-medium text-gray-900">
                  {health?.last_checked ? new Date(health.last_checked).toLocaleString() : 'Never'}
                </p>
              </div>
            </div>
            <button
              onClick={fetchHealth}
              disabled={loading}
              className="bg-whatsapp-green text-white px-6 py-2 rounded-lg hover:bg-green-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{loading ? 'Checking...' : 'Refresh'}</span>
            </button>
          </div>
        </div>

        {/* System Information */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* System Status Summary */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status Summary</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Overall Health</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  health?.database_status === 'healthy' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {health?.database_status === 'healthy' ? 'Operational' : 'Degraded'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">Auto-refresh</span>
                <span className="text-sm text-gray-500">Every 30 seconds</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-600">Monitoring</span>
                <span className="text-sm text-green-600 font-medium">Active</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition flex items-center justify-between">
                <span className="text-gray-700">View System Logs</span>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition flex items-center justify-between">
                <span className="text-gray-700">Database Backup</span>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition flex items-center justify-between">
                <span className="text-gray-700">Clear Cache</span>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminHealth;
